/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useContext } from 'react';
import {
    Icon,
    Button,
    FlexRow,
    FlexCol,
    Stack,
    Text,
    VerticalDivider,
    Inline,
} from 'sarsaparilla';
import PropTypes from 'prop-types';

import { LayoutControlContext } from './DetailedAvailabilityPageLayout';

function MonthYearTitle({ startDate, isMobileApp }) {
    const monthYearTitle = `Starting ${startDate.format('MMM Do YYYY')}`;

    return (
        <Text size={isMobileApp ? 'lg' : 'xl'} fontWeight="semibold">
            {monthYearTitle}
        </Text>
    );
}

MonthYearTitle.propTypes = {
    startDate: PropTypes.any,
    isMobileApp: PropTypes.bool,
};

export default function DetailedAvailabilityNavigator({
    onClearSelection,
    onNextAvailable,
    onNext,
    onPrev,

    nextText = 'Next 5 Days',
    prevText = 'Prev 5 Days',

    isPrevDisabled = false,
    hasNextAvailableDate = true,

    startDate,
    endDate,
}) {
    const { isMobileApp } = useContext(LayoutControlContext);

    return (
        <Stack>
            <FlexRow className="mb-1 mr-0">
                <FlexCol>
                    <MonthYearTitle
                        startDate={startDate}
                        endDate={endDate}
                        isMobileApp={isMobileApp}
                    />
                </FlexCol>
                <FlexCol xs="variable">
                    <Inline>
                        {!!onClearSelection && !isMobileApp && (
                            <>
                                <Button
                                    size="xs"
                                    appearance="link"
                                    onClick={onClearSelection}
                                    iconBeforeElement={
                                        <Icon iconName="close-circle-outline" />
                                    }
                                    screenReaderTextAfter="that were selected"
                                >
                                    Clear Dates
                                </Button>
                                <VerticalDivider />
                            </>
                        )}
                        {!!onPrev && (
                            <>
                                <Button
                                    size="xs"
                                    className="ml-1"
                                    appearance="link"
                                    onClick={onPrev}
                                    isDisabled={isPrevDisabled}
                                    iconBeforeElement={<Icon iconName="chevron-left" />}
                                    screenReaderTextBefore="View"
                                >
                                    {!isMobileApp && prevText}
                                </Button>
                                <VerticalDivider />
                            </>
                        )}
                        {!!onNextAvailable && hasNextAvailableDate && (
                            <>
                                <Button
                                    size="xs"
                                    className="ml-1"
                                    appearance="link"
                                    onClick={onNextAvailable}
                                    screenReaderTextBefore="Go to"
                                    screenReaderTextAfter="dates"
                                >
                                    Next Available
                                </Button>
                                <VerticalDivider />
                            </>
                        )}
                        {!!onNext && (
                            <Button
                                size="xs"
                                className="ml-1 mr-2"
                                appearance="link"
                                onClick={onNext}
                                iconAfterElement={<Icon iconName="chevron-right" />}
                                screenReaderTextBefore="View"
                            >
                                {!isMobileApp && nextText}
                            </Button>
                        )}
                    </Inline>
                </FlexCol>
            </FlexRow>
        </Stack>
    );
}

DetailedAvailabilityNavigator.propTypes = {
    onClearSelection: PropTypes.func,
    onNextAvailable: PropTypes.func,
    onNext: PropTypes.func,
    onPrev: PropTypes.func,

    nextText: PropTypes.string,
    prevText: PropTypes.string,

    isPrevDisabled: PropTypes.bool,

    startDate: PropTypes.any,
    endDate: PropTypes.any,
    hasNextAvailableDate: PropTypes.bool,
};
